/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { getCurrentCategory, CurrentCategoryInput } from '@msdyn365-commerce-modules/retail-actions';
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';

/**
 * CategoryPathStateShareInput Input Action
 */

export class CategoryScheduledAdsInput implements IActionInput {
    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => `categoryScheduledAds`;
    public getCacheObjectType = () => 'categoryScheduledAds';
    public dataCacheType = (): CacheType => 'request';
}

export interface IScheduledAd {
    startTime: string;
    endTime: string;
    imageUrl: string;
    adClickUrl: string;
    adTitle: string;
    currentTileShouldAppearAfterProducts: number;
}

// TODO: Create a data model here or import one to capture the response of the action
export interface ICategoryScheduledAds {
    scheduledAds: IScheduledAd[];
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    return new CategoryScheduledAdsInput();
};

const createGetCurrentCategoryInput = (inputData: IActionContext): CurrentCategoryInput => {
    if (inputData && inputData.requestContext) {
        return new CurrentCategoryInput(inputData.requestContext);
    }
    throw new Error('Please specify categoryId query string in request.');
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
export async function getCategoryScheduledAdsAction(
    input: CategoryScheduledAdsInput,
    context: IActionContext
): Promise<ICategoryScheduledAds> {
    const excelUrl = context.requestContext.app.config.plpAdSchedulingDataURL || '';
    const excelRes = await fetch(excelUrl); //Msdyn365.sendRequest<any>(excelUrl, 'get');
    let currentCategoryResult;
    const scheduledAds: IScheduledAd[] = [];
    try {
        currentCategoryResult = await getCurrentCategory(createGetCurrentCategoryInput(context), context);

        if (currentCategoryResult) {
            const currentCategory = { ...currentCategoryResult };
            const excelDataStr = await excelRes.arrayBuffer();
            const decoder = new TextDecoder('windows-1252'); // or try 'iso-8859-1'
            const tsvText = decoder.decode(excelDataStr);

            console.log('Read Data from Excel', excelDataStr);
            const splittedExcelData = tsvText.split('\n');
            for (const dataRow of splittedExcelData) {
                if (!dataRow || !dataRow.trim()) {
                    continue;
                }
                const rowSplitted = dataRow.trim().split('\t');
                const curl = rowSplitted[0];
                if (currentCategory.RecordId && curl.includes(`${currentCategory.RecordId}`)) {
                    const startTime = rowSplitted[1];
                    const endTime = rowSplitted[2];
                    scheduledAds.push({
                        startTime: startTime,
                        endTime: endTime,
                        imageUrl: rowSplitted[3],
                        adClickUrl: rowSplitted[4],
                        adTitle: rowSplitted[5],
                        currentTileShouldAppearAfterProducts: parseInt(rowSplitted[6], 10)
                    });
                }
            }
            return { scheduledAds: scheduledAds };
        }
    } catch (e) {
        console.log('Error fetching scheduled Ads');
    }
    return { scheduledAds: [] };
}

/**
 * SampleState - create action
 */
export default createObservableDataAction<ICategoryScheduledAds>({
    action: <IAction<ICategoryScheduledAds>>getCategoryScheduledAdsAction,
    input: createInput
});
